import {
  Box,
  Dialog,
  DialogProps,
  IconButton,
  styled as MUIStyled,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusBadge, BadgeIcons } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import { AquariusToast } from "@smartrr/shared/customerPortal/components/AquariusToast";
import { PurchaseStateStatus } from "@smartrr/shared/entities/PurchaseState";
import { defaultCustomerPortalThemeConfig } from "@smartrr/shared/themes/defaultThemeConfig";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import * as Icons from "akar-icons";
import { rgba } from "polished";
import React from "react";
import styled from "styled-components";

import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";

export interface IModalBadgeProps {
  icon: BadgeIcons;
  label: string;
}
export interface IAquariusAdvancedModalProps extends DialogProps {
  /**
   * Text to be shown at the top of modal
   * @deprecated Use `mainTitle` or `subTitle` props to display title
   */
  title?: never;

  /** Text to be shown at the top of modal [h4] */
  mainTitle?: string;

  /** Text to be shown at the top of modal below main title [h3]*/
  subTitle?: string;

  /** Text to be shown at the top of modal below sub title [h3]*/
  caption?: JSX.Element;

  /** Callback fired when the component requests to be closed. */
  onClose: () => void;

  /** Toggle for Toast, autoClose by default after 2 seconds*/
  showToast?: boolean;

  /** Text to be shown within toast*/
  toastText?: string;

  /** If using predetermined badges */
  statusBadges?: IModalBadgeProps[];

  /** Used for importantNotice badge displaying */
  containsOOSItems?: boolean;

  /** Used for importantNotice badge displaying */
  containsLineItemsVariants?: boolean;

  status?: `${PurchaseStateStatus}` | PurchaseStateStatus;

  /** `true` - 20px of padding is applied to bottom and sides (no top) of model contents
   *  `false` - no padding applied
   * @default true */
  contained?: boolean;

  /**
   * modal descriptor for testing modals
   */
  modalDescriptor: string;
}

interface IStyledModalProps extends DialogProps {
  isMobile: boolean;
}

const StyledModal = MUIStyled(({ isMobile, ...props }: IStyledModalProps) => <Dialog {...props} />)<{
  isMobile: boolean;
}>`
  /* Entire Modal + Overlay - NO STYLING HERE*/
  & [class*="MuiDialog-container"] {
    align-items: ${props => (props.isMobile ? "end" : "center")};
    & .MuiDialog-paper {
      /* Modal Card Only */
      border-radius: ${props => props.theme.modernCardRadius}px;
      border: ${props => props.theme.modernCardBorder}px solid
        ${props => props.theme.modernCardBorderColor};
      background-color: ${props => props.theme.modernCardBackground};
      width: 652px;
      ${props =>
        props.isMobile &&
        `
        border-radius: ${props.theme.modernCardRadius}px ${props.theme.modernCardRadius}px 0px 0px;
        width: 100%;
        margin:0px;
        max-height: calc(100% - 20px);
      `};
    }
  }
  & [class*="MuiDialog-backdrop"] {
    background-color: ${props =>
      rgba(
        props.theme.modernPageBackground! ?? defaultCustomerPortalThemeConfig.modernPageBackground,
        0.5
      )} !important; // Forcing the backdrop to display when modal is opened
  }

  & [class*="info-toast"] {
    position: absolute;
    width: 100%;
  }
  div:empty {
    display: unset;
  }
`;

const ModalTitle = MUIStyled(Box)`
  background-color: ${props => props.theme.modernCardBackground};
  border-bottom: 1px solid ${props => props.theme.modernLineDividers};
  padding: 20px 35px 20px 20px;
  position: sticky;
  top: 0;
  z-index: 100;

  & h4,
  & h3 {
    margin: 0;
  }
  & h3 {
    margin-top: 10px;
  }
`;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} unmountOnExit />;
});

const ModalInner = styled(Box).attrs(props => ({
  ...props,
  className: prefixClassName("modal-inner"),
  shouldForwardProp: (prop: string) => prop !== "contained",
}))<{ contained: boolean }>`
  /* No top padding, has to be done by children */
  padding: ${({ contained }) => (contained ? "0px 20px 20px 20px" : "0px")};
`;
export const AquariusModal = ({
  children,
  onClose,
  mainTitle,
  subTitle,
  caption,
  showToast,
  toastText,
  statusBadges,
  status,
  sx,
  containsOOSItems,
  containsLineItemsVariants,
  contained = true,
  modalDescriptor,
  ...props
}: IAquariusAdvancedModalProps): JSX.Element => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const theme = useSmartrrTheme();

  return (
    <StyledModal
      TransitionComponent={Transition}
      className={prefixClassName("modal-wrapper")}
      data-testid="modal-wrapper"
      onClose={onClose}
      isMobile={isMobile}
      {...props}
      sx={{
        ...sx,
      }}
    >
      <div className={prefixClassName("modal", `${modalDescriptor}-modal`)}>
        {!!showToast && <AquariusToast autoClose>{toastText}</AquariusToast>}
        <ModalTitle className={prefixClassName("modal-header", `${modalDescriptor}-modal-header`)}>
          <Stack direction="row" alignItems="center">
            {!!mainTitle && (
              <Typography variant="h4" className={prefixClassName("modal-main-title")}>
                {mainTitle}
              </Typography>
            )}
            {containsOOSItems || containsLineItemsVariants === false ? (
              <AquariusBadge
                icon="exclamationMark"
                label={typedLocalize("badgeStatus.importantNotice")}
                sx={{ marginLeft: "7px" }}
              />
            ) : null}
            {statusBadges ? (
              statusBadges.map((status: IModalBadgeProps, index: number) => {
                return (
                  <AquariusBadge
                    key={status.label}
                    label={status.label}
                    icon={status.icon!}
                    sx={{ marginLeft: "7px" }}
                  />
                );
              })
            ) : status ? (
              <AquariusBadge status={status} sx={{ marginLeft: "7px" }} />
            ) : null}
          </Stack>
          {!!subTitle && (
            <Typography variant="h3" className={prefixClassName("modal-sub-title")}>
              {subTitle}
            </Typography>
          )}
          {caption}
          {!!onClose && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              className={prefixClassName("icon-button", "close")}
              sx={{
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <Icons.Cross strokeWidth={2} size={18} color={theme.modernIconsColor} />
            </IconButton>
          )}
        </ModalTitle>
        <ModalInner contained={contained}>{children}</ModalInner>
      </div>
    </StyledModal>
  );
};

interface IAquariusBasicModalProps extends DialogProps {
  /** Callback fired when the component requests to be closed. */
  onClose: (e: any, reason: any) => void | (() => void);
}

export const AquariusModalBasic = ({ children, onClose, sx, ...props }: IAquariusBasicModalProps) => {
  const isMobile = useMediaQuery("(max-width:600px)");

  return (
    <StyledModal
      TransitionComponent={Transition}
      onClose={onClose}
      isMobile={isMobile}
      className={prefixClassName("modal-wrapper")}
      data-testid="modal-wrapper"
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "transparent !important",
          alignItems: "center",
          boxShadow: "none !important",
          border: "0 !important",
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </StyledModal>
  );
};
